import React, { useState, useEffect } from "react";
import Fuse from "fuse.js";

import Section from "../section/Section";
import ProductGrid from "../product/ProductGrid";
import ProductFilters from "../productFilters/productFilters";
import ProductSearch from "../productFilters/productSearch/productSearch";
import { useDesktopMediaQuery } from "../../utils/helpers";
import { useShopContext } from "../../context/ShopContext";
import { useUser } from "../../context/UserContext";

import styles from "./collectionSection.module.css";
import { useRouter } from "next/router";

const groupedFilters = {
  "size-6-12m": [
    "size-6-9m",
    "size-9-12m",
    "size-9m",
    "size-12m",
    "size-medium",
    "size-1y",
    "size-68/74-(6-9m)",
    "size-74/80-(9-12m)",
  ],
  "size-1-2y": [
    "size-12-18m",
    "size-18m-24m",
    "size-18m",
    "size-24m",
    "size-18-24m",
    "size-xs",
    "size-1.5y",
    "size-2y",
    "size-large",
    "size-80/86-(12-18m)",
    "size-86/92-(18-24m)",
  ],
  "size-0-3m": [
    "size-1m",
    "size-3m",
    "size-56/62-(0-3m)",
    "size-2m",
    "size-small",
  ],
  "size-3-6m": [
    "size-6m",
    "size-4m",
    "size-62/68-(3-6m)",
    "size-62/68-(3-6m)",
    "size-small",
  ],
  "size-2-3y": [
    "size-9m-4y",
    "size-2-3y",
    "size-2-6y",
    "size-36m",
    "size-2",
    "size-s",
    "size-2-4y",
    "size-92/98-(2-3y)",
    "size-3y",
    "size-extra-large",
  ],
  "size-3-4y": [
    "size-9m-4y",
    "size-2-6y",
    "size-3-4y",
    "size-48m",
    "size-2-4y",
    "size-3",
    "size-m",
    "size-4",
    "size-4y",
    "size-98/104-(3-4y)",
  ],
  "size-5-6y": ["size-2-6y", "size-6", "size-6y"],
  "size-6-7y": ["size-6-10y"],
  "size-shoes": [
    "size-19",
    "size-20",
    "size-21",
    "size-22",
    "size-23",
    "size-24",
    "size-25",
    "size-26",
    "size-27",
  ],
};

const _groupTagSlugsByType = (tagSlugs: string[]) => {
  let types = {};

  // ex of slug type_xxx
  tagSlugs.forEach((slug) => {
    let tag = slug.split("-");
    let type = tag.shift();
    types[type] = [...(types[type] || []), tag.join("-")];
  });

  return types;
};

const CollectionSection = () => {
  const [sortBy, setSortBy] = useState(null);
  const [productList, setProductList] = useState(null);
  const { products, subscriptionOption } = useShopContext();
  const { userProfile } = useUser();
  const isDesktop = useDesktopMediaQuery();
  const router = useRouter();
  const [activeFilters, setActiveFilter] = useState([]);
  const [searchTxt, setSearchTxt] = useState<string | null>(null); // TODO: this should be added to the url
  const [indexOfPreviousProd, setIndexOfPreviousProd] = useState<number | null>(
    null
  );

  let emptyProducts = products?.filter((prod) => {
    let emptyStock = prod.product_variants.filter(
      (prodVar) => prodVar.stock < 1
    );
    return emptyStock.length > 0;
  });

  useEffect(() => {
    if (router) {
      if (router.query.q) {
        // @ts-ignore
        setActiveFilter(
          typeof router.query.q === "string" ? [router.query.q] : router.query.q
        );
      } else {
        setActiveFilter([]);
      }

      if (router.query.s) {
        setSearchTxt(router.query.s.toString());
      } else {
        setSearchTxt(null);
      }
    }
  }, [router?.query?.q, router?.query?.s, router]);

  useEffect(() => {
    if (!router.query.select_plan) {
      if (window.sessionStorage.getItem("last-viewed-product")) {
        setIndexOfPreviousProd(
          Number(
            window.sessionStorage.getItem("last-viewed-product").split("+")[1]
          )
        );
        setTimeout(() => {
          // Make sure the products are rendered

          if (
            document.querySelectorAll(
              `#product-${
                window.sessionStorage
                  .getItem("last-viewed-product")
                  ?.split("+")[0]
              }`
            ).length > 0
          ) {
            document
              .querySelector(
                `#product-${
                  window.sessionStorage
                    .getItem("last-viewed-product")
                    .split("+")[0]
                }`
              )
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          window.sessionStorage.removeItem("last-viewed-product");
        }, 300);
      } else {
        setTimeout(() => {
          document
            .querySelector("#collection-shop")
            .scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    }
  }, [subscriptionOption, router?.query?.select_plan, indexOfPreviousProd]);

  useEffect(() => {
    // Filter products
    const onlyShowActive = activeFilters.indexOf("product-not-available") < 0;
    let allActiveFilters = [...activeFilters].filter(
      (itm) => itm !== "product-not-available"
    ); // As this is just a helper filter ,remove it from the rest

    if (onlyShowActive) {
      allActiveFilters.push("product-available");
    }

    activeFilters.forEach((filter) => {
      const groupedIndex = Object.keys(groupedFilters).indexOf(filter);
      if (groupedIndex > -1) {
        allActiveFilters = [...allActiveFilters, ...groupedFilters[filter]];
      }
    });

    if (products) {
      // Add Grouped Filters

      const filtersByType = _groupTagSlugsByType(
        allActiveFilters.filter((filt) => filt !== "previously-rented")
      ); // ignore previously rented filter here
      let filterAvailabilityBySize = false;
      const sizeFilters = allActiveFilters.filter(
        (filter) => filter.indexOf("size") > -1
      );

      if (
        allActiveFilters.indexOf("product-available") > -1 &&
        sizeFilters.length > 0
      ) {
        // Extra Filtering necessary based on sizes
        filterAvailabilityBySize = true;
      }

      let filteredProducts = products.filter((prod) => {
        // Group filters by type
        const tagsByType = _groupTagSlugsByType(prod.filter_tags);

        // let matching = true;

        // go through all of the filter types
        const matchingNr = Object.keys(filtersByType).filter((type) => {
          return !!filtersByType[type].find(
            (tag) => tagsByType[type]?.indexOf(tag) >= 0
          );
        });

        let additionalFiltersPassed = true;

        if (allActiveFilters.indexOf("previously-rented") > -1) {
          additionalFiltersPassed = false;
          prod.product_variants.forEach((prodVar) => {
            if (userProfile.previousProductVariants.indexOf(prodVar.id) > -1) {
              additionalFiltersPassed = true;
            }
          });
        }

        let totalAvailibilityByFilteredSize = 0;

        if (filterAvailabilityBySize) {
          prod.product_variants.forEach((prodVar) => {
            if (prodVar.size && sizeFilters.indexOf(prodVar.size.slug) > -1) {
              totalAvailibilityByFilteredSize += prodVar.stock;
            }
          });
        }

        //@ts-ignore
        prod.colors = prod.filter_tags
          .filter((val) => val.indexOf("color") > -1)
          .join("-");

        return (
          matchingNr.length === Object.keys(filtersByType).length &&
          (!filterAvailabilityBySize || totalAvailibilityByFilteredSize > 0) &&
          additionalFiltersPassed
        );
        // Only match the filter if all the filters match
        // return matchingTags.length === activeFilters.length;
      });

      if (searchTxt) {
        const fuse = new Fuse(filteredProducts, {
          threshold: 0.2,
          keys: ["name", { name: "colors", weight: 2 }],
        });
        filteredProducts = fuse
          .search(searchTxt)
          .map((fuseRes) => fuseRes.item);
      }

      setProductList(filteredProducts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, activeFilters, searchTxt]);

  return (
    <div id="collection-shop">
      <Section disableTabletPadding>
        <div className={styles.columnContainer}>
          {isDesktop && <ProductSearch activeFilters={activeFilters} />}
          <div className={styles.container}>
            <div className={styles.filters}>
              <ProductFilters activeFilters={activeFilters} />
            </div>
            <div className={styles.product}>
              <ProductGrid
                products={productList && productList}
                indexOfPreviousProd={indexOfPreviousProd}
              />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default CollectionSection;
